import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { type FormEvent, useEffect } from "react";
import type { PropertyRecord } from "../api/properties";
import type { RoomRecord } from "../api/rooms";

export type InspectionType = "room" | "kitchen" | "laundry";
export type InspectionResult = "pass" | "fail" | "attention_required";

export type InspectionFormState = {
  targetId: string;
  inspection_date: string;
  result: InspectionResult;
  comments: string;
};

type InspectionModalProps = {
  open: boolean;
  inspectionType: InspectionType;
  properties: PropertyRecord[];
  rooms: RoomRecord[];
  formState: InspectionFormState;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void> | void;
  onInspectionTypeChange: (type: InspectionType) => void;
  onTargetChange: (value: string) => void;
  onDateChange: (value: string) => void;
  onResultChange: (value: InspectionResult) => void;
  onCommentsChange: (value: string) => void;
};

const inspectionLabels: Record<InspectionType, string> = {
  room: "Room inspection",
  kitchen: "Kitchen inspection",
  laundry: "Laundry inspection",
};

export function InspectionModal({
  open,
  inspectionType,
  properties,
  rooms,
  formState,
  isSubmitting,
  onClose,
  onSubmit,
  onInspectionTypeChange,
  onTargetChange,
  onDateChange,
  onResultChange,
  onCommentsChange,
}: InspectionModalProps) {
  useEffect(() => {
    if (!open) {
      return;
    }

    if (inspectionType === "room") {
      if (rooms.length > 0) {
        const currentTargetIsValid = rooms.some(
          (room) => String(room.id) === formState.targetId,
        );

        if (!currentTargetIsValid) {
          onTargetChange(String(rooms[0].id));
        }
      }
      return;
    }

    if (properties.length > 0) {
      const currentTargetIsValid = properties.some(
        (property) => String(property.id) === formState.targetId,
      );

      if (!currentTargetIsValid) {
        onTargetChange(String(properties[0].id));
      }
    }
  }, [
    formState.targetId,
    inspectionType,
    onTargetChange,
    open,
    properties,
    rooms,
  ]);

  const selectedRoom =
    rooms.find((room) => String(room.id) === formState.targetId) ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 5 } }}
    >
      <Box component="form" onSubmit={onSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          Add {inspectionLabels[inspectionType].toLowerCase()}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2.5 }}>
            Capture the inspection outcome and comments for the selected room or
            property.
          </DialogContentText>

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {(["room", "kitchen", "laundry"] as InspectionType[]).map(
                (type) => (
                  <Button
                    key={type}
                    variant={inspectionType === type ? "contained" : "outlined"}
                    onClick={() => onInspectionTypeChange(type)}
                    sx={{
                      borderRadius: 6,
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                  >
                    {inspectionLabels[type]}
                  </Button>
                ),
              )}
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              {inspectionType === "room" ? (
                <Autocomplete
                  fullWidth
                  options={rooms}
                  value={selectedRoom}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(room) => `Room ${room.room_number}`}
                  onChange={(_, value) =>
                    onTargetChange(value ? String(value.id) : "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Room"
                      placeholder="Select a room"
                    />
                  )}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
                />
              ) : null}

              <TextField
                label="Inspection date"
                type="date"
                value={formState.inspection_date}
                onChange={(event) => onDateChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Result</InputLabel>
                <Select
                  label="Result"
                  value={formState.result}
                  onChange={(event) =>
                    onResultChange(event.target.value as InspectionResult)
                  }
                  sx={{ borderRadius: 6 }}
                >
                  <MenuItem value="pass">Pass</MenuItem>
                  <MenuItem value="fail">Fail</MenuItem>
                  <MenuItem value="attention_required">
                    Attention required
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Comments"
                value={formState.comments}
                onChange={(event) => onCommentsChange(event.target.value)}
                multiline
                minRows={2}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit" sx={{ borderRadius: 6 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={isSubmitting}
            sx={{ borderRadius: 6 }}
          >
            {isSubmitting ? "Saving..." : "Save inspection"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
